import type { RiskAssessment, RiskAssessmentFilters } from '@nestjs-boilerplate/types';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/ui/data-table';

import { useRestoreRiskAssessment, useRiskAssessments } from '../api/risk-assessments';
import { Pagination } from './pagination';
import { RiskLevelBadge } from './risk-level-badge';
import { SearchInput } from './search-input';

function formatDate(date: Date | null): string {
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date));
}

interface DeletedAssessmentsContentProps {
  onViewDetails?: (assessment: RiskAssessment) => void;
}

export function DeletedAssessmentsContent({ onViewDetails }: DeletedAssessmentsContentProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Build API filters for deleted assessments
  const apiFilters: Partial<RiskAssessmentFilters> = useMemo(
    () => ({
      status: 'DELETED',
      search: search || undefined,
      page,
      pageSize,
    }),
    [search, page, pageSize],
  );

  // API hooks
  const { data, isLoading, isError } = useRiskAssessments(apiFilters);
  const restoreMutation = useRestoreRiskAssessment();

  // Extract data from API response
  const assessments = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // Handle restore
  const handleRestore = (assessment: RiskAssessment) => {
    restoreMutation.mutate(assessment.id);
  };

  // Reset page when search changes
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const columns: ColumnDef<RiskAssessment>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'optimalKycRef',
      header: 'Optimal KYC Ref',
      cell: ({ row }) => (
        <span className="font-medium text-[#051923]">{row.getValue('optimalKycRef')}</span>
      ),
    },
    {
      accessorKey: 'clientRef',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Client Ref
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span className="text-[#051923]">{row.getValue('clientRef')}</span>,
    },
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      cell: ({ row }) => <span className="text-[#051923]">{row.getValue('clientName')}</span>,
    },
    {
      accessorKey: 'riskLevel',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Client Risk Level
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <RiskLevelBadge level={row.getValue('riskLevel')} />,
    },
    {
      id: 'documentRequest',
      header: 'Document Request Link Generated',
      cell: ({ row }) => {
        const assessment = row.original;
        const hasDocumentRequests =
          assessment.documentRequests && assessment.documentRequests.length > 0;

        if (!hasDocumentRequests) {
          return <span className="text-[#051923]">No</span>;
        }

        return (
          <div className="flex items-center gap-2">
            <span className="text-[#051923]">Yes</span>
            <button
              type="button"
              onClick={() => onViewDetails?.(assessment)}
              className="text-[#051923] underline hover:no-underline"
            >
              View Details
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: 'dateDeleted',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-auto p-0 hover:bg-transparent"
        >
          Date Deleted
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-[#051923]">{formatDate(row.getValue('dateDeleted'))}</span>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const assessment = row.original;

        return (
          <button
            type="button"
            onClick={() => handleRestore(assessment)}
            className="font-medium text-[#051923] underline hover:no-underline"
          >
            Restore
          </button>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[24px] font-semibold text-[#051923]">Deleted Risk Assessments</h1>
        <p className="mt-2 text-[16px] text-muted-foreground">
          Deleted Risk Assessments can be restored for up to 10 days. If not restored during this
          period, the assessments will be permanently deleted in line with our data retention
          policy.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <SearchInput value={search} onChange={handleSearchChange} />
        <span className="text-[14px] text-muted-foreground">{total} Results</span>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading deleted assessments...</div>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="flex items-center justify-center py-12">
          <div className="text-destructive">
            Failed to load deleted assessments. Please try again.
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && assessments.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">No deleted assessments found.</div>
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && assessments.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={assessments}
            enableRowSelection
            getRowId={(row) => row.id}
          />

          {/* Pagination */}
          {total > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </>
      )}
    </div>
  );
}
